import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TrendingDown,
  Timer,
  PackageCheck,
  ShieldCheck,
  ArrowRightLeft,
  X,
  ChevronDown,
  Trophy,
} from "lucide-react";

const getDisplayName = (sup) => {
  if (!sup) return "Select Supplier";
  const name =
    sup?.name || sup?.supplierName || sup?.company || sup?.supplier?.name;
  return name && isNaN(name) ? name : `Supplier ${sup?.sid || sup?.id || "?"}`;
};

// --- 2. THE LOCKED CHAMPION DISPLAY ---
const ChampionDisplay = ({ selected }) => (
  <div className="relative w-full p-6 rounded-[2.2rem] bg-amber-50 dark:bg-amber-900/10 border-2 border-amber-400 flex items-center shadow-xl z-20">
    <div className="flex items-center gap-4 w-full">
      <div className="h-12 w-12 shrink-0 rounded-2xl bg-amber-400 text-white flex items-center justify-center shadow-lg">
        <Trophy size={20} />
      </div>
      <div className="overflow-hidden text-left">
        <p className="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-0.5">
          Current Winner
        </p>
        <h4 className="text-base font-black text-slate-900 dark:text-white truncate">
          {getDisplayName(selected)}
        </h4>
      </div>
    </div>
  </div>
);

// --- 3. THE SELECTABLE CHALLENGER ---
const SupplierSelector = ({ selected, options, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full p-6 rounded-[2.2rem] bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 hover:border-indigo-500 flex items-center justify-between shadow-xl z-20 transition-all"
      >
        <div className="flex flex-row-reverse items-center gap-4 w-full text-right">
          <div className="h-12 w-12 shrink-0 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black">
            {getDisplayName(selected).charAt(0)}
            <div>
              <span>{"   "}</span>
            </div>
          </div>
          <div className="overflow-hidden">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
              Challenger
            </p>
            <h4 className="text-base font-black text-slate-900 dark:text-white truncate">
              {getDisplayName(selected)}
            </h4>
          </div>
          <div>
            <span>{"   "}</span>
          </div>
        </div>
        <ChevronDown
          size={18}
          className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[130]"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-[140] top-full mt-2 w-full bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-700 p-2 overflow-hidden">
            <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
              {options.map((sup, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onSelect(sup);
                    setIsOpen(false);
                  }}
                  className="w-full p-4 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl text-right font-black text-sm text-slate-900 dark:text-white"
                >
                  {getDisplayName(sup)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// --- 4. THE METRIC ROW ---
const MetricRow = ({
  label,
  valA,
  valB,
  icon,
  isWinner,
  beeLevelA,
  beeLevelB,
}) => {
  const getLevelStyle = (lvl) => {
    if (!lvl || lvl === 0) return "text-slate-900 dark:text-white";
    if (lvl <= 2) return "text-emerald-500";
    if (lvl <= 5) return "text-orange-500";
    return "text-rose-500";
  };
  const isBEE = label.includes("B-BBEE");

  return (
    <div className="group/row relative p-8 bg-white dark:bg-slate-800/40 rounded-[2.5rem] border border-slate-100 dark:border-white/5 transition-all duration-500">
      <div className="grid grid-cols-11 items-center relative z-10">
        <div
          className={`col-span-4 text-right pr-8 ${isWinner ? "scale-110" : "opacity-30"
            }`}
        >
          <span
            className={`text-2xl font-black tracking-tighter ${isBEE
              ? getLevelStyle(beeLevelA)
              : "text-slate-900 dark:text-white"
              }`}
          >
            {valA}
          </span>
        </div>
        <div className="col-span-3 flex flex-col items-center gap-3">
          <div className="p-3 bg-slate-900 dark:bg-slate-700 rounded-2xl text-indigo-400 shadow-lg">
            {icon}
          </div>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
            {label}
          </span>
        </div>
        <div
          className={`col-span-4 text-left pl-8 ${!isWinner ? "scale-110" : "opacity-30"
            }`}
        >
          <span
            className={`text-2xl font-black tracking-tighter ${isBEE
              ? getLevelStyle(beeLevelB)
              : "text-slate-900 dark:text-white"
              }`}
          >
            {valB}
          </span>
        </div>
      </div>
    </div>
  );
};

const ComparativeModal = ({ isOpen, onClose, sups, quote, forcedWinner }) => {
  const [challenger, setChallenger] = useState(null);
  const initialSetupDone = useRef(false);

  // Use the winner passed from the dashboard, or find the best one if not provided
  const winner = useMemo(() => {
    if (forcedWinner) {
      // Find the full supplier object in sups that matches the forcedWinner ID
      return (
        sups.find(
          (s) =>
            String(s.id || s.sid) ===
            String(forcedWinner.id || forcedWinner.sid)
        ) || forcedWinner
      );
    }
    if (!sups || sups.length === 0) return null;
    return [...sups].sort((a, b) => getStats(a).cost - getStats(b).cost)[0];
  }, [sups, forcedWinner]);

  useEffect(() => {
    if (isOpen && winner && !initialSetupDone.current) {
      // Sort by the .cost property that ALREADY EXISTS on the objects
      const sorted = [...sups].sort((a, b) => (a.cost || 0) - (b.cost || 0));

      // The winner is index [0], so the runner-up is index [1]
      const runnerUp = sorted[1] || winner;

      setChallenger(runnerUp);
      initialSetupDone.current = true;
    }

    if (!isOpen) {
      initialSetupDone.current = false;
    }
  }, [isOpen, winner, sups]);

  if (!isOpen || !winner || !challenger) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-5xl bg-slate-50 dark:bg-slate-900 rounded-[3.5rem] flex flex-col max-h-[92vh] border border-white/20 shadow-2xl overflow-hidden">
        <div className="p-8 flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter" style={{
              filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
            }}>
              Supplier Arena
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Live Comparison
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-12 w-12 flex items-center justify-center text-slate-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 rounded-2xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto px-8 pb-8 space-y-8 custom-scrollbar">
          <div className="grid grid-cols-11 gap-4 items-center sticky top-0 z-30 py-4 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm">
            <div className="col-span-5">
              <ChampionDisplay selected={winner} />
            </div>
            <div className="col-span-1 text-center font-black italic text-slate-300">
              <span
                className="font-bold text-slate-900"
                style={{
                  filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
                }}
              >VS</span>
            </div>
            <div className="col-span-5">
              <SupplierSelector
                selected={challenger}
                options={sups.filter(
                  (s) => (s.id || s.sid) !== (winner.id || winner.sid)
                )}
                onSelect={setChallenger}
              />
            </div>
          </div>

          <div className="grid gap-4">
            {/* COST */}
            <MetricRow
              label="Total Cost (ZAR)"
              icon={<TrendingDown size={18} />}
              valA={`R ${winner.cost.toLocaleString()}`}
              valB={`R ${challenger.cost.toLocaleString()}`}
              isWinner={winner.cost <= challenger.cost}
            />
            {/* FULFILLMENT */}
            <MetricRow
              label="Fulfillment"
              icon={<PackageCheck size={18} />}
              valA={`${Math.round((winner.coverage / 100) * quote?.length)} / ${quote?.length || 0
                } Items`}
              valB={`${Math.round(
                (challenger.coverage / 100) * quote?.length
              )} / ${quote?.length || 0} Items`}
              isWinner={winner.coverage >= challenger.coverage}
            />
            {/* B-BBEE STATUS with your requested styling */}
            <MetricRow
              label="B-BBEE Status"
              icon={<ShieldCheck size={18} />}
              valA={winner.beeLabel}
              valB={challenger.beeLabel}
              beeLevelA={winner.beeLevel}
              beeLevelB={challenger.beeLevel}
              isWinner={winner.beeLevel <= challenger.beeLevel}
            />
            {/* LEAD TIME - No more 45 days! */}
            <MetricRow
              label="Avg. Lead Time"
              icon={<Timer size={18} />}
              valA={winner.leadTime > 0 ? `${winner.leadTime} Days` : "—"}
              valB={
                challenger.leadTime > 0 ? `${challenger.leadTime} Days` : "—"
              }
              isWinner={winner.leadTime <= challenger.leadTime}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparativeModal;
